import { CurrencyPipe } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
  AccountTransferData,
  AccountTransferService,
  AppBarAction,
  AppBarActionsService,
  AppBarTitleService,
  AppPageService,
  ButtonColor,
  CommonActionKey,
  MessageService,
  PromotionInfoData,
  SimpleDialogComponent,
  SimpleDialogData
} from "common";
import { EMPTY, Subscription } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, finalize, tap } from "rxjs/operators";
import { LoanData, ProductCode } from "../../../loan/domain/models/loan.model";
import {
  AccountTransferSubmittedDialogComponent
} from "../account-transfer-submitted-dialog/account-transfer-submitted-dialog.component";
import { LoanFacade } from '../../../loan/domain/+state/loan.facade';
import { AccountTransferComponentData } from "../account-transfer.model";

@UntilDestroy()
@Component({
    selector: "ifp-account-transfer-payment",
    templateUrl: "./account-transfer-payment.component.html",
    styleUrls: ["./account-transfer-payment.component.scss"],
    standalone: false
})
export class AccountTransferPaymentComponent implements OnInit {
  form: UntypedFormGroup;
  data: AccountTransferComponentData;
  loan: LoanData;
  product = ProductCode;
  paymentFrequency: string;
  promotionInfo: PromotionInfoData;
  accountTransferData: AccountTransferData;
  isCalculating: boolean;

  protected maximumAmount: number;
  protected minimumAmount: number;

  private submitSub: Subscription;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly messageService: MessageService,
    private readonly dialog: MatDialog,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly _accountTransferService: AccountTransferService,
    private readonly _appBarActionsService: AppBarActionsService,
    private readonly _currencyPipe: CurrencyPipe,
    private readonly loanFacade: LoanFacade,
    private readonly appPageService: AppPageService
  ) {
  }

  ngOnInit(): void {
    this.route.data
      .pipe(untilDestroyed(this))
      .subscribe((data: { accountTransfer: AccountTransferComponentData }) => {
        this.data = data.accountTransfer;
        this.loan = data.accountTransfer.loan;
        this.paymentFrequency =
          this._accountTransferService.getLoanOfferPaymentFrequencyLabel(
            this.loan.offer.paymentFrequency
          );

        this.setMinimumAmount();
        this.setMaximumAmount();

        this.formInit();
        this.accountTransferData = this.createAccountTransferData();
      });
    this.appBarTitleService.title = "Make a payment";

    this.form.controls.paymentAmount.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap(() => {
          this.isCalculating = true;
          this.enableSaveAction();
        }),
        debounceTime(500)
      )
      .subscribe((value) => {
        this.data.accountTransfer.amountInput = value;
        this.accountNewBalancesInit();
      });

    this.form.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.enableSaveAction());

    this._appBarActionsService.invoking
      .pipe(untilDestroyed(this))
      .subscribe(this.actionDispatch.bind(this));
  }

  save(): void {
    if (this.isCalculating) {
      return;
    }

    SimpleDialogComponent.show(this.dialog, this.getSaveConfirmationDialogData()).subscribe((actionKey: string) => {
      if(actionKey === CommonActionKey.Confirm) {
        this.data.accountTransfer.submit().subscribe({
          next: () => this.submitSuccess(),
          error: (err) => this.submitError(err),
        });
      }
    });
  }

  private getSaveConfirmationDialogData() : SimpleDialogData {
    return {
      title: "Confirm payment?",
      body: `You are making a payment in the amount of ${this._currencyPipe.transform(this.data.accountTransfer.amountInput)}.`,
      actions: [
        { label: "CANCEL", buttonColor: ButtonColor.Default, actionKey: CommonActionKey.Cancel },
        { label: "CONFIRM", buttonColor: ButtonColor.Primary, actionKey: CommonActionKey.Confirm },
      ],
    }
  }

  cancel(): void {
    this.appPageService.back();
  }

  setAmount(value: number): void {
    this.form.controls.paymentAmount.setValue(value);
  }

  applyPromotionChanged(promotion: PromotionInfoData): void {
    this.promotionInfo = promotion;

    this.setMinimumAmount();
    this.setMaximumAmount();

    this.form.controls.paymentAmount.setValidators(
      this.getPaymentAmountValidators()
    );
    this.form.controls.paymentAmount.updateValueAndValidity();
    if (this.promotionInfo?.payoffAmount)
      this.form.controls.paymentAmount.markAsTouched();
    this.accountNewBalancesInit();
  }

  private formInit(): void {
    this.form = this.formBuilder.group({
      paymentAmount: [undefined, this.getPaymentAmountValidators()],
    });

    this.setAppBarActions();
  }

  private actionDispatch(action: AppBarAction): void {
    const actionHandler: (action: AppBarAction) => void =
      this[action.id].bind(this);
    actionHandler(action);
  }

  private enableSaveAction(): void {
    if (this.form.valid && !this.isCalculating) {
      this._appBarActionsService.enable("save", true);
    } else {
      this._appBarActionsService.enable("save", false);
    }
  }

  private setMaximumAmount(): void {
    this.maximumAmount =
      this.promotionInfo?.payoffAmount ??
      this.data.accountTransfer.amountMaximum;
  }

  private setMinimumAmount(): void {
    this.minimumAmount = Math.min(this.data.accountTransfer.amountMinimum, this.promotionInfo?.payoffAmount ?? this.data.accountTransfer.amountMinimum);
  }

  private submitSuccess(): void {
    this.loanFacade.refreshLoan(this.data.loan.id).subscribe();

    AccountTransferSubmittedDialogComponent.show(
      this.dialog,
      this.data.accountTransfer,
      "Payment submitted",
      "payment")
      .subscribe(() => {
        this.cancel();
      });
  }

  private submitError(error): void {
    this.messageService.error(error);
    this.submitSubClear();
  }

  private submitSubClear(): void {
    if (this.submitSub) this.submitSub.unsubscribe();
    this.submitSub = null;
  }

  private accountNewBalancesInit(): void {
    const value = this.data.accountTransfer.amountInput
      ? parseFloat(this.data.accountTransfer.amountInput)
      : 0;

    this.loanFacade
      .payPreview(this.data.loan.id, value, this.promotionInfo?.promoCode)
      .pipe(
        catchError((err) => {
          this.messageService.error(err);
          return EMPTY;
        }),
        finalize(() => {
          this.isCalculating = false;
          this.enableSaveAction();
        }),
      )
      .subscribe((res) => {
        this.data.accountTransfer.balanceAfter.outstandingBalance = res.newOutstandingBalance;
        this.data.accountTransfer.balanceAfter.availableBalance = res.newAvailableFunds;
        this.data.accountTransfer.balanceAfter.upcomingPaymentAmount = res.newUpcomingPaymentAmount;
        this.data.accountTransfer.balanceAfter.upcomingPaymentDate = res.newUpcomingPaymentDate;
        this.data.accountTransfer.promotionInfo = res.promotionInfo;

        this.accountTransferData = this.createAccountTransferData();
      });
  }

  private setAppBarActions(): void {
    this._appBarActionsService.actions = [];
    this._appBarActionsService.actions.push({
      id: "cancel",
      label: "Cancel",
      buttonType: "button",
    });
    this._appBarActionsService.actions.push({
      id: "save",
      label: "Continue",
      disabled: !this.form.dirty,
      buttonType: "submit",
      buttonAppearance: "flat",
      buttonColor: "primary",
    });
  }

  private getPaymentAmountValidators(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.min(this.minimumAmount),
      Validators.max(this.maximumAmount),
    ];
  }

  private createAccountTransferData(): AccountTransferData {
    return {
      loan: {
        id: this.data.loan.id,
        description: this.data.loan.description,
        paymentAmount: this.data.accountTransfer.loanInfo?.paymentAmount
      },
      balanceAfter: this.data.accountTransfer.balanceAfter,
      promotionInfo: this.data.accountTransfer.promotionInfo,
      from: this.data.accountTransfer.from,
      to: this.data.accountTransfer.to,
      fee: this.data.accountTransfer.fee,
      disbursedAmount: this.data.accountTransfer.disbursedAmount,
      thirdPartyPayoffAmount: this.data.accountTransfer.thirdPartyPayoffAmount,
      type: this.data.accountTransfer.type,
    };
  }
}
